import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

export default function Privacy() {
  return (
    <PageLayout>
      <Card className="max-w-4xl mx-auto">
        <CardHeader>
          <CardTitle className="text-3xl">Privacy Policy</CardTitle>
          <p className="text-sm text-muted-foreground">Last updated: {new Date().toLocaleDateString()}</p>
        </CardHeader>
        <CardContent className="prose prose-sm dark:prose-invert max-w-none space-y-6">
          <section>
            <h2 className="text-xl font-semibold mb-3">1. Information We Collect</h2>
            <p>We collect the following types of information:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Account Information:</strong> Email address, password (encrypted), and account preferences</li>
              <li><strong>Usage Data:</strong> Calculator usage, SuperCalc queries, favorites, and session information</li>
              <li><strong>Payment Information:</strong> Processed securely through third-party payment processors; we do not store full credit card details</li>
              <li><strong>Technical Data:</strong> IP address, browser type, device information, and access times</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">2. How We Use Your Information</h2>
            <p>We use collected information for:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Providing and improving our services</li>
              <li>Processing payments and managing subscriptions</li>
              <li>Tracking SuperCalc usage for billing and cost management</li>
              <li>Communicating with you about your account and service updates</li>
              <li>Preventing fraud and ensuring security</li>
              <li>Analyzing usage patterns to improve our calculators and tools</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">3. SuperCalc Data</h2>
            <p>
              When you use SuperCalc, we store your queries and the AI-generated solutions for:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Billing purposes (tracking usage costs for refund calculations)</li>
              <li>Service improvement and quality assurance</li>
              <li>Your personal query history (accessible only to you and administrators)</li>
            </ul>
            <p className="mt-2">
              Your queries are sent to AI service providers (OpenAI, Google) for processing. 
              Please review their privacy policies for information on how they handle data.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">4. Data Sharing</h2>
            <p>We do not sell your personal information. We may share data with:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Service Providers:</strong> AI providers (OpenAI, Google), payment processors, and hosting services</li>
              <li><strong>Legal Requirements:</strong> When required by law or to protect our rights and safety</li>
              <li><strong>Business Transfers:</strong> In the event of a merger, acquisition, or asset sale</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">5. Data Security</h2>
            <p>
              We implement industry-standard security measures to protect your data, including:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Encrypted connections (HTTPS/TLS)</li>
              <li>Secure password hashing</li>
              <li>Regular security audits</li>
              <li>Access controls and authentication</li>
            </ul>
            <p className="mt-2">
              However, no method of transmission over the internet is 100% secure. We cannot guarantee absolute security.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">6. Your Rights</h2>
            <p>You have the right to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Access your personal data</li>
              <li>Correct inaccurate data</li>
              <li>Request deletion of your account and data</li>
              <li>Export your data (favorites, query history)</li>
              <li>Opt out of marketing communications</li>
              <li>Object to data processing in certain circumstances</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">7. Data Retention</h2>
            <p>We retain your data for as long as your account is active or as needed to provide services. Specifically:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Account Data:</strong> Until you delete your account</li>
              <li><strong>SuperCalc Usage:</strong> Retained for billing, refunds, and compliance (minimum 90 days after subscription ends)</li>
              <li><strong>Backups:</strong> May persist in backups for up to 90 days after deletion</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">8. Cookies and Tracking</h2>
            <p>
              We use essential cookies for authentication and session management. We do not use advertising or third-party tracking cookies.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">9. Children's Privacy</h2>
            <p>
              CalcLab is not directed to children under 13. We do not knowingly collect information from children under 13. 
              If you believe we have collected such information, please contact us immediately.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">10. International Users</h2>
            <p>
              Your information may be transferred to and processed in countries other than your own. 
              By using our service, you consent to such transfers.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">11. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of material changes via email 
              or through the service. Continued use after changes constitutes acceptance.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">12. Contact Us</h2>
            <p>
              For privacy-related questions or to exercise your rights, please contact us through our support page 
              (available to paid members) or email us directly.
            </p>
          </section>
        </CardContent>
      </Card>
    </PageLayout>
  );
}
